import { ArrowCircleDown16Regular, ArrowCircleUp16Filled, ArrowCircleUp16Regular, EqualCircleRegular, } from "@fluentui/react-icons";
import classNames from "owa-classnames";
import { observer } from "owa-mobx-react";
import { copilotInboxPriorityIconForMLStyles, copilotInboxPriorityIconForSettingsStyles, copilotInboxPriorityIconForRPStyles, copilotInboxPriorityIconForRPStylesCozyAndCompact, copilotInboxHighPriorityIconColor, copilotInboxLowPriorityIconColor, copilotInboxNormalPriorityColor, copilotInboxPriorityHighContrastColor, iconContainer, } from "./CopilotInboxPriorityIcon.scss";
import getCopilotPrioritizeSettings from "owa-mail-copilot-settings/lib/selectors/getCopilotPrioritizeSettings";
import type { MailboxInfo } from "owa-client-types";
import { highPriorityIconTooltip, lowPriorityIconTooltip, } from "./CopilotInboxPriorityIcon.locstring.json";
import loc from "owa-localize";
import { getDensityModeCssClass } from "owa-fabric-theme";
import { isCopilotHighPriority, isCopilotLowPriority } from "../utils/priorityUtils";
import type { FunctionComponent } from "react";
import { isPrioritizeOnDeliveryOn } from "owa-copilot-settings-utils";

interface CopilotInboxPriorityIconProps {
    copilotInboxScore?: number;
    priorityOverride?: "high" | "low" | "normal";
    scenario: "ML" | "Settings" | "RP" | "PrioritySort" | "OnboardedCallout" | "PrioritizeBar";
    mailboxInfo?: MailboxInfo;
    isUnread?: boolean;
    copilotPrioritizeOnDeliveryOn?: boolean;
}

const defaultExport: FunctionComponent<CopilotInboxPriorityIconProps> = observer(function CopilotInboxPriorityIcon(props: CopilotInboxPriorityIconProps) {
    const { copilotInboxScore = 0, scenario, priorityOverride, mailboxInfo, isUnread = true, copilotPrioritizeOnDeliveryOn, } = props;
    
    // Fast path: if there's no priority override and the score indicates no
    // priority, skip the expensive isPrioritizeOn checks entirely.
    // isCopilotHighPriority/isCopilotLowPriority are cheap numeric comparisons.
    const hasScoreBasedPriority = isCopilotHighPriority(copilotInboxScore) || isCopilotLowPriority(copilotInboxScore);
    if (!priorityOverride && !hasScoreBasedPriority) {
        return null;
    }
    
    const prioritizeOnDeliveryOn = copilotPrioritizeOnDeliveryOn ??
        (mailboxInfo ? isPrioritizeOnDeliveryOn(mailboxInfo) : false);
    const applyLowPriorityLabel = mailboxInfo
        ? getCopilotPrioritizeSettings(mailboxInfo).applyLowPriorityLabel
        : null;
    
    const isHighPriority = priorityOverride === "high" ||
        (mailboxInfo && prioritizeOnDeliveryOn && isCopilotHighPriority(copilotInboxScore));
    
    const isLowPriority = priorityOverride === "low" ||
        (mailboxInfo &&
            prioritizeOnDeliveryOn &&
            (applyLowPriorityLabel || scenario === "PrioritySort") &&
            isCopilotLowPriority(copilotInboxScore));
    
    const isNormalPriority = priorityOverride === "normal";
    
    let scenarioStyles = null;
    switch (scenario) {
        case "ML":
        case "PrioritySort":
        case "OnboardedCallout":
            scenarioStyles = copilotInboxPriorityIconForMLStyles;
            break;
        case "Settings":
            scenarioStyles = copilotInboxPriorityIconForSettingsStyles;
            break;
        case "RP":
            scenarioStyles = getDensityModeCssClass(copilotInboxPriorityIconForRPStyles, copilotInboxPriorityIconForRPStylesCozyAndCompact, copilotInboxPriorityIconForRPStylesCozyAndCompact);
            break;
    }
    
    const iconStyles = classNames(isHighPriority && copilotInboxHighPriorityIconColor, isLowPriority && copilotInboxLowPriorityIconColor, isNormalPriority && copilotInboxNormalPriorityColor, copilotInboxPriorityHighContrastColor, scenarioStyles);
    
    let icon = null;
    
    if (isLowPriority) {
        icon = <ArrowCircleDown16Regular className={iconStyles}/>;
    }
    else if (isHighPriority) {
        if (isUnread) {
            icon = <ArrowCircleUp16Filled className={iconStyles}/>;
        }
        else {
            icon = <ArrowCircleUp16Regular className={iconStyles}/>;
        }
    }
    else if (isNormalPriority) {
        icon = <EqualCircleRegular className={iconStyles}/>;
    }
    
    if (!icon) {
        return null;
    }
    
    const iconTitle = isHighPriority
        ? loc(highPriorityIconTooltip)
        : loc(lowPriorityIconTooltip);
    
    if (scenario === "ML" && mailboxInfo && !prioritizeOnDeliveryOn) {
        return null;
    }
    
    return (<div className={iconContainer} title={scenario === "ML" ? iconTitle : undefined}>
                {icon}
            </div>);
}, "CopilotInboxPriorityIcon");
export default defaultExport;
